// client/src/services/offlineCacheBridge.ts
//
// PONTE CODA OFFLINE ↔ CACHE (Ott 2026).
//
// `apiFetch` accoda le scritture fallite per assenza di rete (outbox), e il
// replay le rigioca quando la connessione torna. Restavano però disallineate
// le DUE cache da cui le schermate leggono i dati:
//
//   1. `cachedFetch` (Dexie/IndexedDB — `services/offlineCache.ts`), usata da
//      Navbar, stato abbonamento, mappe, A/B test;
//   2. le query attive di React Query (memoria).
//
// Conseguenza: dopo un'azione fatta offline la schermata continuava a mostrare
// lo stato vecchio, e al ritorno della rete il replay riusciva ma la UI
// restava stantia finché non scadeva il TTL (fino a 30 minuti) o si
// ricaricava l'app.
//
// Il ponte:
//   - all'ACCODAMENTO marca stantie le entry della cache locale della risorsa
//     toccata e invalida le query attive SENZA refetch (offline fallirebbe);
//   - a fine REPLAY invalida le stesse risorse con refetch attivo, così i dati
//     appena scritti sul server vengono riletti.
//
// L'aggancio avviene in `main.tsx` con `registerOfflineCacheBridge(queryClient)`.

import type { QueryClient } from "@tanstack/react-query";
import { invalidateCacheByPrefix } from "@/services/offlineCache";
import {
  OFFLINE_QUEUED_EVENT,
  OFFLINE_SYNC_COMPLETED_EVENT,
  type QueuedRequestDetail,
} from "@/utils/offlineRequestQueue";

/**
 * Risorse il cui prefisso di cache NON coincide con i primi tre segmenti
 * dell'URL (es. `/api/users/me/badges` vive sotto `/api/users/me`).
 */
const EXPLICIT_PREFIXES: Array<[RegExp, string]> = [
  [/^\/api\/users\/me/, "/api/users/me"],
  [/^\/api\/profile/, "/api/profile/me"],
  [/^\/api\/events\/nearby/, "/api/events"],
];

/**
 * Prefissi di chiave nella cache locale toccati da una scrittura.
 *
 * La cache locale è indicizzata per URL della risorsa, quindi il prefisso è
 * `/api/<risorsa>`:
 *
 * `/api/events/12/join`   → `['/api/events']`
 * `/api/garage/bikes/42`  → `['/api/garage']`
 * `/api/users/me/badges`  → `['/api/users/me']`  (risorsa annidata)
 */
export function cachePrefixesForPath(path: string): string[] {
  const clean = (path.split("?")[0] || "").replace(/\/+$/, "");
  if (!clean.startsWith("/api/")) return [];

  for (const [pattern, prefix] of EXPLICIT_PREFIXES) {
    if (pattern.test(clean)) return [prefix];
  }

  const segments = clean.split("/").filter(Boolean); // ['api','garage','bikes','42']
  if (segments.length < 2) return [];
  return [`/${segments.slice(0, 2).join("/")}`];
}

/** Marca stantie le entry locali delle risorse toccate (senza toccare la rete). */
export async function invalidateLocalCacheForPaths(paths: string[]): Promise<void> {
  const prefixes = new Set<string>();
  for (const path of paths) {
    for (const prefix of cachePrefixesForPath(path)) prefixes.add(prefix);
  }
  await Promise.allSettled(
    [...prefixes].map((prefix) => invalidateCacheByPrefix(prefix)),
  );
}

export interface OfflineCacheBridge {
  /** Stacca gli ascoltatori (usato dai test e in caso di hot reload). */
  dispose: () => void;
}

/**
 * Collega la coda offline alle cache. Da chiamare una sola volta, subito dopo
 * la creazione del `QueryClient`.
 */
export function registerOfflineCacheBridge(
  client?: QueryClient | null,
): OfflineCacheBridge {
  if (typeof window === "undefined") return { dispose: () => {} };

  const handleQueued = (event: Event) => {
    const detail = (event as CustomEvent<QueuedRequestDetail>).detail;
    if (!detail?.path) return;

    // L'azione è in coda: i dati mostrati non riflettono più il server.
    // Niente refetch: la rete è (quasi sempre) assente in questo momento.
    client?.invalidateQueries({ refetchType: "none" });
    void invalidateLocalCacheForPaths([detail.path]);
  };

  const handleSynced = (event: Event) => {
    const detail = (event as CustomEvent<{ paths?: string[] }>).detail;
    const paths = detail?.paths ?? [];
    if (paths.length === 0) return;

    // Il replay ha scritto sul server: ora le query attive devono rileggere.
    client?.invalidateQueries({ refetchType: "active" });
    void invalidateLocalCacheForPaths(paths);
  };

  window.addEventListener(OFFLINE_QUEUED_EVENT, handleQueued);
  window.addEventListener(OFFLINE_SYNC_COMPLETED_EVENT, handleSynced);

  return {
    dispose: () => {
      window.removeEventListener(OFFLINE_QUEUED_EVENT, handleQueued);
      window.removeEventListener(OFFLINE_SYNC_COMPLETED_EVENT, handleSynced);
    },
  };
}
